import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import { Document } from '@/modules/document/types'
import { useAppSelector } from '@/redux/store'
import { Tooltip } from '@mui/material'
import clsx from 'clsx'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import TodoDetail from '@/modules/todo/components/todo-detail'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconLink from '@assets/images/icons/link.svg'
import IconCross from '@assets/images/icons/close.svg'
import { dispatch } from '@/redux/ducks/lists'
import { refreshList } from '@/redux/ducks/document/list'
import { fetchDocumentDetails, putDocument, updateDocument } from '@/redux/ducks/document/drawer'
import lang from '@/lang'
import DocumentType from '@/enums/document-type.enum'
import { openDocumentDrawer } from '@/redux/ducks/document/documentDrawerHelpers'
import { SquareIconButton } from '@/modules/base/components/buttons'
import { ConfirmModal } from '@/modules/base/components/modal'
import ExpenseService from '@/modules/expense/ExpenseService'
import RevenueService from '@/modules/revenue/revenueService'
import { useNavigate } from 'react-router'
import { fetchListAction } from '@/redux/ducks/lists/requests/documentList'
import TodoSigningDrawer from '@/modules/todo/components/todo-signing-drawer'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import TodoService from '@/modules/todo/todoService'
import { setActiveTodoId } from '@/redux/ducks/todo/list'
import { user } from 'ampersand-app'
import { errorCodes, SIGNATURE_FIELD_TYPES } from '@/modules/base/util/constants'
import { toast } from '@/modules/base/components/toast/toastConfig'

interface DocumentAssignmentRouterProps {
  document?: Document
  readOnly?: boolean
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  documentAssignmentRouter: {
  },
  tooltip: {
    fontSize: '12px',
  },
  assigned: {
    marginTop: 25,
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '& a, & span': {
      fontSize: 14,
      marginLeft: 5,
    },
    '& span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '90%',
      display: 'inline-block',
    },
    '&.disabled': {
      pointerEvents: 'none',
      '& a': {
        color: theme.grey1,
      },
    },
  },
  drawer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flexGrow: 1,
  },
  linkWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& a, & span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'inline-block',
      fontSize: theme.fontSizes.default,
      marginLeft: 5,
      maxWidth: 'calc(100% - 30px)',
    },
  },
  cross: {
    marginLeft: 'auto',
  },
}))

const TodoLink = ({ todoFile, onClick }) => {
  const classes = useStyles()

  const label = [lang.documents.todo, lang.todoList.typeLabels[todoFile.type], `${todoFile.title ?? todoFile.content}`].join(' ')

  return (
    <Tooltip classes={{ tooltip: classes.tooltip }} title={label}>
      <div className={classes.linkWrapper} >
        <SVGIcon svg={IconLink} width={'12px'} height={'12px'} />
        <a onClick={() => onClick(todoFile.todoId)}>{label}</a>
      </div>
    </Tooltip>
  )
}

const TransactionLink = ({ readOnly = false }: { readOnly?: boolean }) => {
  const classes = useStyles()
  const navigate = useNavigate()

  const revenue = useAppSelector((state) => state.document.drawer.document.revenue)
  const expense = useAppSelector((state) => state.document.drawer.document.expense)
  const cancellationId = useAppSelector((state) => state.document.drawer.document.cancellationId)
  const invoiceId = useAppSelector((state) => state.document.drawer.document.invoiceId)
  const type = useAppSelector((state) => state.document.drawer.document.type)
  const receiptType = useAppSelector((state) => state.document.drawer.document.receiptType)
  const number = useAppSelector((state) => state.document.drawer.document.number)
  const document = useAppSelector((state) => state.document.drawer.document)

  const [fileToUnlink, setFileToUnlink] = useState(null)

  if (!expense && !revenue && !invoiceId && !cancellationId) return null

  const getLabel = () => {
    if (type === DocumentType.EXPENSE || receiptType === DocumentType.EXPENSE) {
      return [lang.defaultTitle, lang.transactionTypes.expense, expense.receiptNumber].join(' ')
    } else if (type === DocumentType.REVENUE || receiptType === DocumentType.REVENUE) {
      return [lang.defaultTitle, lang.otherRevenue, revenue.receiptNumber].join(' ')
    } else if (type === DocumentType.CANCELLATION || receiptType === DocumentType.CANCELLATION) {
      return [lang.transactionTypes.cancellation, number].join(' ')
    } else if (type === DocumentType.INVOICE || receiptType === DocumentType.INVOICE) {
      return [lang.transactionTypes.invoice, number].join(' ')
    }
  }

  const label = getLabel()

  const onClick = () => {
    if (type === DocumentType.EXPENSE || receiptType === DocumentType.EXPENSE) {
      navigate(`/expenses/edit/${expense.id}`)
    } else if (type === DocumentType.REVENUE || receiptType === DocumentType.REVENUE) {
      navigate(`/revenues/edit/${revenue.id}`)
    } else if (type === DocumentType.CANCELLATION || receiptType === DocumentType.CANCELLATION) {
      navigate(`/cancellations/${cancellationId}`)
    } else if (type === DocumentType.INVOICE || receiptType === DocumentType.INVOICE) {
      navigate(`/invoices/${invoiceId}`)
    }
  }

  const onUnlinkConfirm = () => {
    if (!fileToUnlink) return
    const { expenses = [], revenues = [], expense, revenue } = document
    const isExpense = type === DocumentType.EXPENSE || receiptType === DocumentType.EXPENSE
    const collection = isExpense ? expenses : revenues
    const deleteMethod = isExpense ? ExpenseService.deleteExpenseReceipt : RevenueService.deleteRevenueReceipt
    const entry = collection.find(e => e.id === fileToUnlink)
    if (!entry && !expense && !revenue) {
      setFileToUnlink(null)
      return
    }

    const receiptIdToDelete = entry
      ? entry[isExpense ? 'expenseReceipt' : 'revenueReceipt'].id
      : isExpense ? expense?.receiptId : revenue?.receiptId

    if (!receiptIdToDelete) {
      setFileToUnlink(null)
      return toast.showToast({ type: 'error', message: lang.documents.errors.unlinkReceiptError })
    }

    deleteMethod(receiptIdToDelete)
      .then(() => {
        const updatedCollection = []
        dispatch(updateDocument({ key: isExpense ? 'expenses' : 'revenues', value: updatedCollection }))
        dispatch(putDocument({ ...document, [isExpense ? 'expense' : 'revenue']: undefined }))
        dispatch(refreshList())
        dispatch(fetchListAction())
        dispatch(fetchDocumentDetails({
          documentId: document.id,
          analyze: false,
          rename: false,
          analysisDone: true,
          analysisActive: false,
          preserveTags: [],
        }))
      })
      .finally(() => {
        setFileToUnlink(null)
      })
  }

  const actionId = expense?.id ?? revenue?.id
  const documentType = receiptType === DocumentType.EXPENSE ? lang.transactionTypes.expense : lang.transactionTypes.revenue
  return (
    <>
      <Tooltip classes={{ tooltip: classes.tooltip }} title={label}>
        <div className={classes.linkWrapper}>
          <SVGIcon svg={IconLink} width="12px" height="12px" />
          <a onClick={onClick}>{label}</a>
          {!readOnly && (receiptType === DocumentType.EXPENSE || receiptType === DocumentType.REVENUE)
            ? <div className={classes.cross}>
              <SquareIconButton
                tooltip={lang.todo.iconButtons.remove}
                size="small"
                icon={IconCross}
                onClick={() => setFileToUnlink(actionId!)}
              />
            </div>
            : null
          }
        </div>
      </Tooltip>

      <ConfirmModal
        open={fileToUnlink !== null}
        title={lang.documents.unlinkReceiptTitle(documentType)}
        description={
          <span
            dangerouslySetInnerHTML={{
              __html: lang.documents.linkDeleteConfirmText.replace(
                '{{document}}',
                documentType,
              ),
            }}
          />
        }
        onConfirm={onUnlinkConfirm}
        onAbort={() => setFileToUnlink(null)}
      />
    </>
  )
}
const SignedFileLink = () => {
  const classes = useStyles()
  const navigate = useNavigate()

  const metaData = useAppSelector((state) => state.document.drawer.document.metaData)
  const signedFileId = useAppSelector((state) => state.document.drawer.document.signedFileId)
  const signedFile = useAppSelector((state) => state.document.drawer.document.signedFile)
  const document = useAppSelector((state) => state.document.drawer.document)

  if (!signedFileId) return null

  const onClick = () => {
    if (metaData.orderId) {
      navigate(`/orders/${metaData.orderId}`)
    } else if (metaData.offerId) {
      navigate(`/offers/${metaData.offerId}`)
    } else if (signedFileId) {
      openDocumentDrawer({ documentId: signedFileId })
    }
  }

  const getLabel = () => {
    if (metaData?.orderId) return lang.transactionTypes.order + ` ${document?.orderNumber}`
    if (metaData?.offerId) return lang.transactionTypes.offer + ` ${document?.offerNumber}`
    return [lang.originalDocumentLabel, signedFile?.decodedName].join(' ')
  }

  const label = getLabel()

  return (
    <Tooltip classes={{ tooltip: classes.tooltip }} title={label}>
      <div className={classes.linkWrapper}>
        <SVGIcon svg={IconLink} width={'12px'} height={'12px'} />
        <a onClick={onClick}>{label}</a>
      </div>
    </Tooltip>
  )
}

const SignatureFileLink = ({ signatureFile }) => {
  const classes = useStyles()

  const label = lang.documents.signatureAssignmentLabel(signatureFile.decodedName)

  return (
    <Tooltip classes={{ tooltip: classes.tooltip }} title={label}>
      <div className={classes.linkWrapper}>
        <SVGIcon svg={IconLink} width={'12px'} height={'12px'} />
        <a onClick={() => openDocumentDrawer({ documentId: signatureFile.id })}>{label}</a>
      </div>
    </Tooltip>
  )
}

const AssignmentText = () => {
  const classes = useStyles()

  const externalOwnerSystem = useAppSelector((state) => state.document.drawer.document.externalOwnerSystem)

  if (!externalOwnerSystem) return null

  return (
    <Tooltip classes={{ tooltip: classes.tooltip }} title={lang.documents.isAssignedToWindows}>
      <div className={classes.linkWrapper}>
        <SVGIcon svg={IconLink} width={'12px'} height={'12px'} />
        <span>{lang.windowsProductName}</span>
      </div>
    </Tooltip>
  )
}

const DocumentAssignmentRouter: React.FC<DocumentAssignmentRouterProps> = (props) => {
  const classes = useStyles(props)
  const { readOnly = false } = props

  const linkedTodoFiles = useAppSelector((state) => state.document.drawer.document.linkedTodoFiles)
  const signedFileId = useAppSelector((state) => state.document.drawer.document.signedFileId)
  const revenue = useAppSelector((state) => state.document.drawer.document.revenue)
  const expense = useAppSelector((state) => state.document.drawer.document.expense)
  const cancellationId = useAppSelector((state) => state.document.drawer.document.cancellationId)
  const invoiceId = useAppSelector((state) => state.document.drawer.document.invoiceId)
  const externalOwnerSystem = useAppSelector((state) => state.document.drawer.document.externalOwnerSystem)

  const { setSigningMode: setSigningView, resetContext } = useSignatureContext()

  const [todo, setTodo] = useState(null)
  const [todoDrawerOpen, setTodoDrawerOpen] = useState(false)

  const loadTodo = async(id) => {
    try {
      const todo = await TodoService.getById(id)
      setTodo(todo)
      const isResponsibleUser = todo?.users?.some(u => u.id === user.userId)
      if (isResponsibleUser && !todo?.isSeen) {
        TodoService.markAsSeen(todo)
      }
    } catch (error) {
      let message = lang.defaultErrorMessage
      if (error?.body?.meta?.tenant[0]?.code === errorCodes.NOT_ALLOWED) {
        message = lang.todo.collaborationDeletedErrorMessage
      }

      toast.showToast({ type: 'error', message })
    }
  }

  useEffect(() => {
    if (todo?.type !== SIGNATURE_FIELD_TYPES.SIGNATURE) return setTodoDrawerOpen(!!todo)
  }, [todo])

  const isSignatureTodo = useMemo(() => todo?.type === SIGNATURE_FIELD_TYPES.SIGNATURE, [todo])

  const onClickTodo = (id) => {
    loadTodo(id)
  }

  const handleDrawerClose = () => {
    resetContext()
    setSigningView(false)
    setTodo(null)
    dispatch(setActiveTodoId(null))

    const isResponsibleUser = todo?.users?.some(u => u.id === user.userId)
    if (isResponsibleUser && !todo?.isSeen) {
      TodoService.markAsSeen(todo)
    }
  }

  if (!signedFileId && !revenue && !expense && !cancellationId && !invoiceId && !externalOwnerSystem && !linkedTodoFiles?.length) {
    return null
  }

  return (
    <div className={classes.documentAssignmentRouter}>
      <div className={clsx(classes.assigned)}>
        <label>{lang.documents.drawer.labels.assignedTo}</label>
        {linkedTodoFiles?.length > 0 && linkedTodoFiles.map((todoFile) => <TodoLink key={todoFile.id} todoFile={todoFile} onClick={onClickTodo} />)}
        <TransactionLink readOnly={readOnly} />
        <SignedFileLink />
        <AssignmentText />
      </div>
      <SimpleDrawer
        open={todoDrawerOpen}
        noHeader
        onClose={() => setTodoDrawerOpen(false)}
        className={classes.drawer}>
        <TodoDetail
          todo={todo}
          onClose={() => setTodoDrawerOpen(false)}
        />
      </SimpleDrawer>
      {isSignatureTodo
        ? <TodoSigningDrawer
          todo={todo}
          onClose={handleDrawerClose} />
        : null
      }
    </div>
  )
}

export default DocumentAssignmentRouter
